import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../utils/api';
import { getWorkingDays } from '../utils/workingDays';
import '../styles/admin-figures.css';

const MONTHS = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function formatCurrency(value) {
  return Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function formatShortDate(date) {
  const [, month, day] = String(date).split('-');
  return `${day}/${month}`;
}

export function calculateDayAverages(months, maxDays) {
  return Array.from({ length: maxDays }, (_, dayIndex) => {
    const values = months
      .map(month => Number(month.days[dayIndex]?.ca_ht))
      .filter(value => Number.isFinite(value) && value > 0);
    return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
  });
}

export function calculateDayMedians(months, maxDays) {
  return Array.from({ length: maxDays }, (_, dayIndex) => {
    const values = months
      .map(month => Number(month.days[dayIndex]?.ca_ht))
      .filter(value => Number.isFinite(value) && value > 0)
      .sort((a, b) => a - b);
    return quantile(values, 0.5);
  });
}

export function buildAverageChart(averages) {
  const values = averages.filter(value => Number.isFinite(value) && value > 0);
  if (values.length === 0) return null;

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const spread = maxValue - minValue || Math.max(maxValue * 0.2, 100);
  const rawStep = spread / 4;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const niceFactor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const step = niceFactor * magnitude;
  const domainMin = Math.max(0, Math.floor((minValue - step * 0.5) / step) * step);
  const domainMax = Math.ceil((maxValue + step * 0.5) / step) * step;
  const ticks = [];
  for (let value = domainMin; value <= domainMax + step / 2; value += step) ticks.push(value);
  return { domainMin, domainMax, ticks };
}

export function calculateYearlyDayAverages(months, maxDays) {
  const byYear = new Map();
  for (const month of months) {
    if (!byYear.has(month.year)) byYear.set(month.year, []);
    byYear.get(month.year).push(month);
  }
  return [...byYear.entries()]
    .sort(([yearA], [yearB]) => yearA - yearB)
    .map(([year, yearMonths]) => ({ year, values: calculateDayAverages(yearMonths, maxDays) }));
}

function quantile(sortedValues, percentile) {
  if (sortedValues.length === 0) return null;
  const position = (sortedValues.length - 1) * percentile;
  const lowerIndex = Math.floor(position);
  const fraction = position - lowerIndex;
  const lower = sortedValues[lowerIndex];
  const upper = sortedValues[lowerIndex + 1] ?? lower;
  return lower + (upper - lower) * fraction;
}

export function calculateMonthThresholds(months) {
  const values = months
    .flatMap(month => month.days.map(day => Number(day.ca_ht)))
    .filter(value => Number.isFinite(value) && value > 0)
    .sort((a, b) => a - b);
  if (values.length === 0) return null;
  return {
    q1: quantile(values, 0.25),
    median: quantile(values, 0.5),
    q3: quantile(values, 0.75),
  };
}

export const CA_CATEGORIES = [
  { id: 'calm', label: 'Calme', color: '#64748b' },
  { id: 'normal', label: 'Normal', color: '#2563eb' },
  { id: 'busy', label: 'Soutenu', color: '#f59e0b' },
  { id: 'peak', label: 'Pic attendu', color: '#dc2626' },
];

export function categorizeCa(value, thresholds) {
  if (!thresholds || !Number.isFinite(value) || value <= 0) return null;
  if (value < thresholds.q1) return CA_CATEGORIES[0];
  if (value < thresholds.median) return CA_CATEGORIES[1];
  if (value < thresholds.q3) return CA_CATEGORIES[2];
  return CA_CATEGORIES[3];
}

export function calculatePlanningMonthSeries(months, selectedMonth) {
  const selectedMonths = months.filter(month => month.month === selectedMonth);
  const maxDays = selectedMonths.reduce((max, month) => Math.max(max, month.days.length), 0);
  return {
    selectedMonths,
    maxDays,
    averages: calculateDayAverages(selectedMonths, maxDays),
    medians: calculateDayMedians(selectedMonths, maxDays),
    yearlySeries: calculateYearlyDayAverages(selectedMonths, maxDays),
    thresholds: calculateMonthThresholds(selectedMonths),
  };
}

export function buildPlanningCalendar(year, month, dayIndicators, thresholds, holidays = []) {
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const firstDayOffset = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;
  const cells = Array.from({ length: firstDayOffset }, () => null);
  const workingDays = new Set(getWorkingDays(year, month, holidays));
  let workedDayIndex = 0;

  for (let day = 1; day <= daysInMonth; day += 1) {
    const closed = !workingDays.has(day);
    const indicator = closed ? null : dayIndicators[workedDayIndex] ?? null;
    cells.push({
      day,
      closed,
      workedDay: closed ? null : workedDayIndex + 1,
      value: indicator,
      category: categorizeCa(indicator, thresholds),
    });
    if (!closed) workedDayIndex += 1;
  }

  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const YEAR_COLORS = ['#3b82f6', '#f97316', '#22c55e', '#a855f7', '#e11d48', '#06b6d4', '#eab308'];

function DecisionAnalysisChart({ averages, yearlySeries, thresholds = null, dayIndicators = averages, title = 'Analyse des moyennes', subtitle = 'Moyenne totale et comparaison annuelle du CA HT par rang de jour travaillé' }) {
  const series = useMemo(() => [
    { id: 'total', label: 'Moyenne totale', values: averages, color: '#64748b' },
    ...yearlySeries.map((item, index) => ({
      id: String(item.year),
      label: String(item.year),
      values: item.values,
      color: YEAR_COLORS[index % YEAR_COLORS.length],
    })),
  ], [averages, yearlySeries]);
  const [hiddenIds, setHiddenIds] = useState(() => new Set());
  const seriesSignature = series.map(item => item.id).join('|');

  useEffect(() => { setHiddenIds(new Set()); }, [seriesSignature]);

  const visibleSeries = series.filter(item => !hiddenIds.has(item.id));
  const allValues = visibleSeries.flatMap(item => item.values).filter(value => Number.isFinite(value) && value > 0);
  const scale = buildAverageChart(allValues);
  const dayCount = Math.max(1, ...series.map(item => item.values.length));
  const width = Math.max(760, dayCount * 43);
  const height = 370;
  const margin = { top: 28, right: 28, bottom: 68, left: 78 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const range = scale ? scale.domainMax - scale.domainMin || 1 : 1;
  const xFor = dayIndex => margin.left + (dayCount <= 1 ? plotWidth / 2 : (dayIndex / (dayCount - 1)) * plotWidth);
  const yFor = value => margin.top + (((scale?.domainMax || 1) - value) / range) * plotHeight;
  const categoryRanges = thresholds ? [
    { ...CA_CATEGORIES[0], rangeLabel: `< ${Math.round(thresholds.q1).toLocaleString('fr-FR')} €` },
    { ...CA_CATEGORIES[1], rangeLabel: `${Math.round(thresholds.q1).toLocaleString('fr-FR')}–${Math.round(thresholds.median).toLocaleString('fr-FR')} €` },
    { ...CA_CATEGORIES[2], rangeLabel: `${Math.round(thresholds.median).toLocaleString('fr-FR')}–${Math.round(thresholds.q3).toLocaleString('fr-FR')} €` },
    { ...CA_CATEGORIES[3], rangeLabel: `≥ ${Math.round(thresholds.q3).toLocaleString('fr-FR')} €` },
  ] : [];

  const segmentsFor = values => {
    const segments = [];
    let current = [];
    values.forEach((value, monthIndex) => {
      if (Number.isFinite(value) && value > 0) current.push({ monthIndex, value });
      else if (current.length) { segments.push(current); current = []; }
    });
    if (current.length) segments.push(current);
    return segments;
  };

  const toggleSeries = id => {
    setHiddenIds(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="admin-figures-chart-panel analysis">
      <div className="admin-figures-chart-header">
        <div><strong>{title}</strong><span>{subtitle}</span></div>
      </div>
      {thresholds && (
        <div className="admin-figures-category-legend" aria-label="Catégories de chiffre d’affaires">
          {categoryRanges.map(category => <span key={category.id}><i style={{ background: category.color }} />{category.label} <em>{category.rangeLabel}</em></span>)}
          <small>Seuils recalculés sur les journées du mois affiché</small>
        </div>
      )}
      <div className="admin-figures-analysis-layout">
        <div className="admin-figures-chart-legend" aria-label="Sélection des courbes">
          {series.map(item => (
            <button key={item.id} type="button" className={hiddenIds.has(item.id) ? 'hidden' : ''} onClick={() => toggleSeries(item.id)} aria-pressed={!hiddenIds.has(item.id)}>
              <i style={{ background: item.color }} />
              <span>{item.label}</span>
            </button>
          ))}
          <small>Cliquez pour masquer ou afficher une courbe.</small>
        </div>
        <div className="admin-figures-chart-scroll">
          {!scale ? <div className="admin-figures-chart-empty">Sélectionnez au moins une courbe.</div> : <svg className="admin-figures-chart" style={{ minWidth: width }} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Moyenne totale et comparaison annuelle du CA HT moyen par jour travaillé">
          {scale.ticks.map(tick => {
            const y = yFor(tick);
            return (
              <g key={tick}>
                <line x1={margin.left} x2={width - margin.right} y1={y} y2={y} className="chart-grid-line" />
                <text x={margin.left - 10} y={y + 4} textAnchor="end" className="chart-y-label">{Math.round(tick).toLocaleString('fr-FR')} €</text>
              </g>
            );
          })}
          <line x1={margin.left} x2={margin.left} y1={margin.top} y2={height - margin.bottom} className="chart-axis" />
          <line x1={margin.left} x2={width - margin.right} y1={height - margin.bottom} y2={height - margin.bottom} className="chart-axis" />
          {dayIndicators.map((value, index) => {
            const category = categorizeCa(value, thresholds);
            if (!category) return null;
            return (
              <g key={index} className="chart-day-indicator">
                <rect x={xFor(index) - 14} y={height - margin.bottom + 14} width="28" height="25" rx="5" style={{ fill: category.color }} />
                <text x={xFor(index)} y={height - margin.bottom + 31} textAnchor="middle">J{index + 1}</text>
                <title>{`Jour ${index + 1} · Prévision médiane ${formatCurrency(value)} · ${category.label}`}</title>
              </g>
            );
          })}
          {visibleSeries.map(item => {
            return (
              <g key={item.id}>
                {segmentsFor(item.values).map((segment, segmentIndex) => (
                  <polyline key={segmentIndex} points={segment.map(point => `${xFor(point.monthIndex)},${yFor(point.value)}`).join(' ')} className="chart-year-line" style={{ stroke: item.color }} />
                ))}
                {item.values.map((value, monthIndex) => {
                  if (!Number.isFinite(value) || value <= 0) return null;
                  const category = item.id === 'total' ? null : categorizeCa(value, thresholds);
                  return (
                    <circle key={monthIndex} cx={xFor(monthIndex)} cy={yFor(value)} r="4.5" className="chart-year-point" style={{ stroke: item.color }}>
                      <title>{`${item.label} · Jour ${monthIndex + 1} : ${formatCurrency(value)}${category ? ` · ${category.label}` : ''}`}</title>
                    </circle>
                  );
                })}
              </g>
            );
          })}
          </svg>}
        </div>
      </div>
    </div>
  );
}

function PlanningCalendar({ year, month, dayIndicators, thresholds, holidays }) {
  const cells = useMemo(
    () => buildPlanningCalendar(year, month, dayIndicators, thresholds, holidays),
    [year, month, dayIndicators, thresholds, holidays]
  );
  const weekdays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return (
    <div className="admin-figures-planning-calendar">
      <div className="admin-figures-calendar-header">
        <div><strong>Prévision du planning · {MONTHS[month]} {year}</strong><span>Montpellier · dimanches et jours fériés fermés</span></div>
      </div>
      <div className="admin-figures-calendar-grid">
        {weekdays.map(day => <div key={day} className="calendar-weekday">{day}</div>)}
        {cells.map((cell, index) => cell ? (
          <div
            key={cell.day}
            className={`calendar-day${cell.closed ? ' closed' : ''}${cell.category ? ' categorized' : ''}`}
            style={cell.category ? { '--planning-color': cell.category.color } : undefined}
          >
            <span className="calendar-date">{cell.day}</span>
            {cell.closed ? <small>Fermé</small> : <><strong>J{cell.workedDay}</strong>{cell.category && <small>{cell.category.label}</small>}</>}
            {cell.category && <title>{`${cell.day} ${MONTHS[month]} · J${cell.workedDay} · Prévision médiane ${formatCurrency(cell.value)} · ${cell.category.label}`}</title>}
          </div>
        ) : <div key={`empty-${index}`} className="calendar-day empty" />)}
      </div>
    </div>
  );
}

export default function AdminFiguresPage({ restricted = false, lockedStoreId = null }) {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [data, setData] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [loadingStores, setLoadingStores] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [view, setView] = useState(restricted ? 'planning-help' : 'figures');
  const [planningMonth, setPlanningMonth] = useState(() => new Date().getMonth() + 1);
  const menuRef = useRef(null);

  useEffect(() => {
    const closeMenu = event => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, []);

  useEffect(() => {
    let active = true;
    api.getStores()
      .then(items => {
        if (!active) return;
        const visibleItems = restricted && lockedStoreId
          ? items.filter(store => Number(store.id) === Number(lockedStoreId))
          : restricted ? items.slice(0, 1) : items;
        setStores(visibleItems);
        const savedId = localStorage.getItem('adminFiguresStoreId');
        const initial = restricted ? visibleItems[0] : visibleItems.find(store => String(store.id) === savedId) || visibleItems[0];
        setStoreId(initial ? String(initial.id) : '');
      })
      .catch(err => active && setError(err.message))
      .finally(() => active && setLoadingStores(false));
    return () => { active = false; };
  }, [restricted, lockedStoreId]);

  useEffect(() => {
    if (restricted) setView('planning-help');
  }, [restricted]);

  useEffect(() => {
    if (!storeId) {
      setData(null);
      return;
    }
    let active = true;
    setLoadingData(true);
    setError('');
    localStorage.setItem('adminFiguresStoreId', storeId);
    Promise.all([api.adminKpiDailyRanks(storeId), api.getHolidays(storeId)])
      .then(([result, holidayRows]) => {
        if (!active) return;
        setData(result);
        setHolidays(holidayRows);
      })
      .catch(err => {
        if (!active) return;
        setData(null);
        setError(err.message);
      })
      .finally(() => active && setLoadingData(false));
    return () => { active = false; };
  }, [storeId]);

  const averages = useMemo(() => {
    return data ? calculateDayAverages(data.months, data.max_days) : [];
  }, [data]);
  const yearlySeries = useMemo(() => data ? calculateYearlyDayAverages(data.months, data.max_days) : [], [data]);
  const planningSeries = useMemo(
    () => data ? calculatePlanningMonthSeries(data.months, planningMonth) : null,
    [data, planningMonth]
  );
  const selectedStore = stores.find(store => String(store.id) === storeId);
  const showMontpellierCalendar = selectedStore?.name?.toLocaleLowerCase('fr-FR').includes('montpellier');
  const planningYear = new Date().getFullYear();

  return (
    <main className="admin-figures-page">
      <div className={`admin-figures-topbar${restricted ? ' restricted' : ''}`}>
        {!restricted && <div>
          <h1>📊 Chiffres</h1>
          <p>{view === 'analysis'
            ? 'Comparaison des moyennes totales et annuelles'
            : view === 'planning-help'
              ? `Aide à la décision pour le mois de ${MONTHS[planningMonth]}`
              : 'CA HT par rang de jour travaillé pour chaque mois importé'}</p>
        </div>}
        {view === 'planning-help' && (
          <div className="admin-figures-month-nav" aria-label="Navigation entre les mois">
            <button type="button" onClick={() => setPlanningMonth(month => month === 1 ? 12 : month - 1)} aria-label="Mois précédent">‹</button>
            <strong>{MONTHS[planningMonth]}</strong>
            <button type="button" onClick={() => setPlanningMonth(month => month === 12 ? 1 : month + 1)} aria-label="Mois suivant">›</button>
          </div>
        )}
        <div className="admin-figures-controls">
          {!restricted && <div className="admin-figures-view-menu" ref={menuRef}>
            <button type="button" className="admin-figures-menu-button" onClick={() => setMenuOpen(open => !open)} aria-label="Ouvrir le menu des graphiques" aria-expanded={menuOpen}>☰</button>
            {menuOpen && (
              <div className="admin-figures-view-dropdown">
                <button type="button" className={view === 'figures' ? 'active' : ''} onClick={() => { setView('figures'); setMenuOpen(false); }}>▦ Chiffres</button>
                <button type="button" className={view === 'analysis' ? 'active' : ''} onClick={() => { setView('analysis'); setMenuOpen(false); }}>📈 Analyse</button>
                <button type="button" className={view === 'planning-help' ? 'active' : ''} onClick={() => { setView('planning-help'); setMenuOpen(false); }}>🗓️ Aide Planning</button>
              </div>
            )}
          </div>}
          <label className="admin-figures-store">
            <span>Magasin</span>
            {restricted ? <strong>{selectedStore?.name || 'Chargement…'}</strong> : <select value={storeId} onChange={event => setStoreId(event.target.value)} disabled={loadingStores || stores.length === 0}>
              {stores.length === 0 && <option value="">Aucun magasin</option>}
              {stores.map(store => <option key={store.id} value={store.id}>{store.name}</option>)}
            </select>}
          </label>
        </div>
      </div>

      <section className="admin-figures-card">
        {view === 'figures' && <div className="admin-figures-note">
          Chaque colonne correspond au 1er, 2e, 3e… jour ayant un CA HT dans le mois, quelle que soit sa date ou son jour de semaine.
        </div>}

        {(loadingStores || loadingData) && <div className="admin-figures-state loading-pulse">Chargement des chiffres…</div>}
        {!loadingStores && !loadingData && error && <div className="admin-figures-state error">{error}</div>}
        {!loadingStores && !loadingData && !error && data?.months.length === 0 && (
          <div className="admin-figures-state">Aucun mois importé pour ce magasin.</div>
        )}

        {!loadingStores && !loadingData && !error && data?.months.length > 0 && (
          view === 'analysis' ? (
            <DecisionAnalysisChart averages={averages} yearlySeries={yearlySeries} />
          ) : view === 'planning-help' ? (
            planningSeries?.selectedMonths.length > 0
              ? <>
                  <DecisionAnalysisChart
                    key={`planning-${planningMonth}`}
                    averages={planningSeries.averages}
                    dayIndicators={planningSeries.medians}
                    yearlySeries={planningSeries.yearlySeries}
                    thresholds={planningSeries.thresholds}
                    title={`Aide Planning · ${MONTHS[planningMonth]}`}
                    subtitle={`Moyenne totale et comparaison des ${MONTHS[planningMonth].toLowerCase()} disponibles par année`}
                  />
                  {showMontpellierCalendar && <PlanningCalendar
                    year={planningYear}
                    month={planningMonth}
                    dayIndicators={planningSeries.medians}
                    thresholds={planningSeries.thresholds}
                    holidays={holidays}
                  />}
                </>
              : <div className="admin-figures-state">Aucune donnée importée pour le mois de {MONTHS[planningMonth]}.</div>
          ) : (
            <div className="admin-figures-table-scroll">
            <table className="admin-figures-table">
              <thead>
                <tr>
                  <th className="month-column">Mois</th>
                  {Array.from({ length: data.max_days }, (_, index) => <th key={index}>Jour {index + 1}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.months.map(month => (
                  <tr key={`${month.year}-${month.month}`}>
                    <th className="month-column" scope="row">
                      <span>{MONTHS[month.month]}</span>
                      <small>{month.year}</small>
                    </th>
                    {Array.from({ length: data.max_days }, (_, index) => {
                      const day = month.days[index];
                      return (
                        <td key={index} className={day ? '' : 'empty'}>
                          {day ? <><strong>{formatCurrency(day.ca_ht)}</strong><small>{formatShortDate(day.date)}</small></> : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th className="month-column">Moyenne</th>
                  {averages.map((average, index) => (
                    <td key={index}>{average === null ? '—' : <strong>{formatCurrency(average)}</strong>}</td>
                  ))}
                </tr>
              </tfoot>
            </table>
            </div>
          )
        )}
      </section>
    </main>
  );
}
